import { Button } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useActionRequest } from "../../../../common/hooks/api";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { NetworkRequest } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import {
  DefProcessingActivityDto,
  LawfulBasis,
  ProcessingActivityDto,
} from "../../../../common/service/server/dto/ProcessingActivityDto";
import { Loading } from "../../../components";
import { GDPRProcessingActivitiesDialog } from "../../../components/dialog/GDPRProcessingActivitiesDialog";
import { QuestionLabel } from "../../../components/get-compliant/survey/QuestionContainer";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { ProcessingActivitySettingsTable } from "../../../components/organisms/ProcessingActivitySettingsTable";
import { SurveyGateButton } from "../../../components/organisms/survey/SurveyGateButton";
import { usePrimaryOrganizationId } from "../../../hooks/useOrganization";
import {
  useDefaultActivityCheckboxes,
  useProcessingActivites,
} from "../../../hooks/useProcessingActivities";
import { Routes } from "../../../root/routes";

import {
  EEA_UK_SURVEY_NAME,
  EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG,
  EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG,
} from "../../../surveys/eeaUkSurvey";
import { LawfulBasisExtendedOptionsVisibility } from "../../../components/organisms/ProcessingActivityLawfulBasisSelect";

export const GDPR_LAWFUL_BASES_SURVEY = "gdpr-lawful-bases-survey";

export const ConsumerCollectionGdprLawfulBases: React.FC = () => {
  // Variables
  const organizationId = usePrimaryOrganizationId();

  const [readyToRender, setReadyToRender] = useState(false);

  const [allActivities, activityRequest, refreshActivities] =
    useProcessingActivites(organizationId);
  const defCheckboxes = useDefaultActivityCheckboxes(organizationId, allActivities, "EEA_UK", [
    "EEA_UK",
  ]);
  const activities = useMemo(
    () => allActivities?.filter((a) => a?.regions?.includes("EEA_UK")) ?? [],
    [allActivities],
  );
  const {
    answers,
    setAnswer,
    request: surveyRequest,
  } = useSurvey(organizationId, GDPR_LAWFUL_BASES_SURVEY, () => refreshActivities());

  const { answers: eeaUkAnswers } = useSurvey(organizationId, EEA_UK_SURVEY_NAME);

  const [paDialog, setPADialog] = useState(false);
  const openPADialog = () => setPADialog(true);
  const closePADialog = () => setPADialog(false);

  // Effects
  useEffect(() => {
    if (
      NetworkRequest.areFinished(
        surveyRequest,
        defCheckboxes.requests.defaultActivities,
        activityRequest,
      )
    ) {
      setReadyToRender(true);
    }
  }, [surveyRequest, defCheckboxes.requests.defaultActivities, activityRequest]);

  let extendedOptionsVisibility: LawfulBasisExtendedOptionsVisibility = {
    PUBLIC_INTEREST: eeaUkAnswers[EXTRA_LAWFUL_BASES_PUBLIC_INTEREST_SLUG] === "true",
    VITAL_INTEREST_OF_INDIVIDUAL: eeaUkAnswers[EXTRA_LAWFUL_BASES_VITAL_INTEREST_SLUG] === "true",
  };

  const { fetch: updateLawfulBasis } = useActionRequest({
    api: ({ activityId, bases }: { activityId: string; bases: LawfulBasis[] }) =>
      Api.processingActivities.patchUpdateProcessingActivity(organizationId, activityId, {
        name: undefined,
        lawfulBases: bases,
        regions: undefined,
      }),
    onSuccess: () => refreshActivities(),
  });

  if (!readyToRender) {
    return (
      <GetCompliantPageWrapper>
        <PageHeader titleContent="Lawful Bases" />
        <Loading />
      </GetCompliantPageWrapper>
    );
  }

  return (
    <GetCompliantPageWrapper>
      <PageHeader titleContent="Lawful Bases" />

      <QuestionLabel label="The GDPR allows businesses to collect and process data only for legitimate, lawful purposes. In this step, we'll walk through the lawful bases in the GDPR and determine which ones apply to your processing activities." />
      <SurveyGateButton slug="lawful-bases-get-started" survey={answers} updateResponse={setAnswer}>
        <div>
          <p className="mt-lg text-weight-medium">
            Listed below are the core lawful reasons for processing data. These apply to just about
            every business, so we’ll include them as lawful bases for your business.
          </p>

          <p className="text-color-primary mb-0 text-weight-medium">
            Complying with legal obligations
          </p>
          <p className="mt-0">
            Data is processed in order to meet legal or regulatory obligations. For example,
            complying with tax or privacy laws. You use TrueVault Polaris, so this one is easy!
          </p>

          <p className="text-color-primary mb-0 text-weight-medium">Fulfilling contracts</p>
          <p className="mt-0">
            Data is processed to honor a promise or commitment to the individual whose data you are
            processing. For example, fulfilling customer orders.
          </p>

          <p className="text-color-primary mb-0 text-weight-medium">Legitimate interests</p>
          <p className="mt-0">
            Data is processed for a business operational purpose, such as for sales, marketing, data
            storage, customer support, website management, or improving products/services, among
            others.
          </p>

          <p className="text-color-primary mb-0 text-weight-medium">Consent</p>
          <p className="mt-0">
            Consent is the lawful basis for certain processing activities, like data collection via
            website cookies and a few other less common processing activities.
          </p>
        </div>

        <SurveyGateButton
          slug="lawful-basis-reviewed-main-lawful-bases"
          survey={answers}
          updateResponse={setAnswer}
          label="Got it"
        >
          <>
            <div className="mt-lg">
              <QuestionLabel
                label={"Let's define your processing activities in a little more detail."}
              />
            </div>
            <div className="my-lg">
              <QuestionLabel
                label={
                  <>
                    <p className="text-weight-regular">
                      Based on your answers in{" "}
                      <a href={Routes.getCompliant.dataCollection.consumerPurposes}>
                        Collection Purposes
                      </a>
                      , these are the data processing activities your company is currently engaged
                      in.
                    </p>
                    <p className="text-weight-regular">
                      Use the table below to assign at least one lawful basis to each of your
                      processing activities. We’ve pre-filled Lawful Bases for your Processing
                      Activities according to our research. If you added any additional Processing
                      Activities, please select a Lawful Basis below. Feel free to edit the Lawful
                      Bases for the pre-selected Processing Activities if you need to.
                    </p>
                  </>
                }
              />
              <ProcessingActivitySettingsTable
                activities={activities}
                region="EEA_UK"
                onChange={updateLawfulBasis}
                extendedOptionsVisibility={extendedOptionsVisibility}
                disableRemovalForMandatory
                onRemove={async () => await refreshActivities()}
              />
            </div>
            <div className="my-lg">
              <Button variant="contained" className="my-xs" onClick={openPADialog}>
                Edit GDPR Data Processing Activities
              </Button>
            </div>

            <SurveyGateButton
              slug="lawful-basis-processing-activities-done"
              survey={answers}
              updateResponse={setAnswer}
              label="Done"
            />
          </>
        </SurveyGateButton>
      </SurveyGateButton>

      <GDPRProcessingActivitiesDialog open={paDialog} onClose={closePADialog} />
    </GetCompliantPageWrapper>
  );
};

export type CustomActivityCheckboxesProps = {
  customActivities: ProcessingActivityDto[];
  checked: string[];
  setChecked: (defaultId: string, checked: boolean) => void;
};

export type DefaultActivityCheckboxesProps = {
  defaults: DefProcessingActivityDto[];
  checked: string[];
  setChecked: (defaultId: string, checked: boolean) => void;
};

export type UseCustomActivityInputs = {
  applyChanges: () => Promise<void>;
  clearActivities: () => void;
  props: CustomActivityInputsProps;
};

export const useCustomActivityInputs = (
  orgId: string,
  activities: ProcessingActivityDto[],
): UseCustomActivityInputs => {
  const [localActivities, setLocalActivities] = useState<CustomActivity[]>([]);

  // Extract and memoize custom activities from remote
  const remoteActivities = useMemo(() => {
    return (activities || [])
      .filter((activity) => !activity.defaultId && !activity.autocreationSlug)
      .map((activity) => ({
        activityId: activity.id,
        name: activity.name,
      }));
  }, [activities]);

  // Set our initial local activities whenever we get updates from remote
  useEffect(() => {
    const activities = remoteActivities?.length == 0 ? [{ name: "" }] : [...remoteActivities];
    setLocalActivities(activities);
  }, [remoteActivities]);

  const handleAdd = () => {
    const updated = [...localActivities];
    updated.push({ name: "" });
    setLocalActivities(updated);
  };

  const handleRemove = (idx: number) => {
    if (idx == 0) {
      return;
    }
    const updated = [...localActivities];
    updated.splice(idx, 1);
    setLocalActivities(updated);
  };

  const handleUpdate = (idx: number, val: string) => {
    const current = localActivities[idx];
    const updated = [...localActivities];
    updated.splice(idx, 1, { ...current, name: val });
    setLocalActivities(updated);
  };

  const applyChanges = async () => {
    const created = localActivities.filter((a) => !a.activityId && a.name);
    const updated = localActivities.filter((a) => {
      const custom = remoteActivities.find((ca) => a.activityId && ca.activityId == a.activityId);
      return Boolean(custom);
    });
    const activityIds = localActivities.map((a) => a.activityId);
    const removed = remoteActivities.filter((ca) => !activityIds.includes(ca.activityId));

    const createPr = created.map((a) =>
      Api.processingActivities.createProcessingActivity(orgId, {
        name: a.name,
        defaultId: undefined,
        lawfulBases: undefined,
        regions: ["EEA_UK"],
      }),
    );
    const updatePr = updated.map((a) =>
      Api.processingActivities.patchUpdateProcessingActivity(orgId, a.activityId, {
        name: a.name,
        lawfulBases: undefined,
        regions: undefined,
      }),
    );
    const removePr = removed.map((a) =>
      Api.processingActivities.deleteProcessingActivityRegion(orgId, a.activityId, ["EEA_UK"]),
    );

    const all: Promise<any>[] = [...createPr, ...updatePr, ...removePr];

    await Promise.all(all);
  };

  const clearActivities = () => {
    setLocalActivities([]);
  };

  return {
    applyChanges,
    clearActivities,
    props: {
      activities: localActivities,
      onAdd: handleAdd,
      onRemove: handleRemove,
      onUpdate: handleUpdate,
    },
  };
};

type CustomActivityInputsProps = {
  activities: CustomActivity[];
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, val: string) => void;
};

type CustomActivity = {
  name: string;
  activityId?: string;
};
